/**
 * <wishlist-button>
 * Native wishlist toggle for the heart icon previously rendered as
 * `.heart-littlebutton` via Vue directives. It owns its own click handling and
 * heart state and talks directly to the global `window.wishlistProvider`,
 * removing the dependency on the Vue app.
 *
 * Markup (both icons always rendered; toggled via display):
 *   <wishlist-button class="heart-littlebutton"
 *       data-handle="..." data-product-id="..." variant-id="...">
 *     <i class="hvg-icon-heart-o"></i>
 *     <i class="hvg-icon-heart-f" style="display:none"></i>
 *   </wishlist-button>
 *
 * State source: window.wishlistProvider.currentWishlist.wishlistItems, matched
 * by variantId. Re-renders on the global `wishlist:updated` / `wishlist:ready`
 * events dispatched by the provider, and on `variant-id` attribute changes.
 *
 * PDP gallery instances carry data-variant-source="pdp" and additionally listen
 * to the global `enroute:variant-changed` event so the heart follows the
 * currently selected variant.
 */
class WishlistButton extends HTMLElement {
  static get observedAttributes() {
    return ["variant-id"];
  }

  connectedCallback() {
    this._onWishlistUpdated = () => this._render();
    this._onClick = (event) => this._handleClick(event);
    window.addEventListener("wishlist:updated", this._onWishlistUpdated);
    window.addEventListener("wishlist:ready", this._onWishlistUpdated);
    this.addEventListener("click", this._onClick);

    if (this.dataset.variantSource === "pdp") {
      this._onVariantChanged = (event) => {
        if (event.detail && event.detail.variantId) this.setVariant(event.detail.variantId);
      };
      window.addEventListener("enroute:variant-changed", this._onVariantChanged);
    }

    this._render();
  }

  disconnectedCallback() {
    window.removeEventListener("wishlist:updated", this._onWishlistUpdated);
    window.removeEventListener("wishlist:ready", this._onWishlistUpdated);
    this.removeEventListener("click", this._onClick);
    if (this._onVariantChanged) {
      window.removeEventListener("enroute:variant-changed", this._onVariantChanged);
    }
  }

  attributeChangedCallback(name, oldValue, newValue) {
    if (name === "variant-id" && oldValue !== newValue) this._render();
  }

  get variantId() {
    return this.getAttribute("variant-id");
  }

  setVariant(id) {
    this.setAttribute("variant-id", String(id));
  }

  get provider() {
    return window.wishlistProvider;
  }

  _inWishlist() {
    const provider = this.provider;
    if (!provider || !provider.currentWishlist || !provider.currentWishlist.id || provider.currentWishlist.id.length === 0) {
      return false;
    }
    const id = this.variantId + "";
    return provider.currentWishlist.wishlistItems.some((el) => el.variantId + "" === id);
  }

  _render() {
    const iconO = this.querySelector(".hvg-icon-heart-o, .erj-icon-heart-o");
    const iconF = this.querySelector(".hvg-icon-heart-f, .erj-icon-heart-f");
    if (!iconO || !iconF) return;

    // Mirror the old `v-if="!wishlistProvider.storeObject.error"` guard.
    if (this.provider && this.provider.storeObject && this.provider.storeObject.error) {
      this.style.display = "none";
      return;
    }
    this.style.display = "";

    this._paint(this._inWishlist());
  }

  /** Toggle which heart icon is visible without touching provider state. */
  _paint(inList) {
    const iconO = this.querySelector(".hvg-icon-heart-o, .erj-icon-heart-o");
    const iconF = this.querySelector(".hvg-icon-heart-f, .erj-icon-heart-f");
    if (iconO) iconO.style.display = inList ? "none" : "";
    if (iconF) iconF.style.display = inList ? "" : "none";
  }

  _handleClick(event) {
    event.preventDefault(); // stop the wrapping <a> from navigating
    event.stopPropagation(); // stop slider / card click handlers

    const provider = this.provider;
    if (!provider || (provider.storeObject && provider.storeObject.error)) return;

    const needLogin = Boolean(window._erj_ && window._erj_.settings && window._erj_.settings.need_wishlist_login);
    const loggedIn = typeof customerId !== "undefined" && customerId !== null && customerId !== "";
    if (!loggedIn && needLogin) {
      window.dispatchEvent(new CustomEvent("enroute:open-login-modal", { detail: { source: "wishlist-toggle" } }));
      return;
    }

    const variantId = this.variantId;
    const existing = provider.currentWishlist.wishlistItems.find((el) => el.variantId + "" === variantId + "");

    if (existing) {
      this._paint(false); // optimistic
      provider.removeItem(
        existing,
        () => provider.deleteFromWishlist(existing),
        (err) => {
          this._render(); // rollback on failure
          console.log(err);
        }
      );
    } else {
      this._paint(true); // optimistic
      provider.addItemToWishlist(
        { handle: this.dataset.handle, productId: this.dataset.productId, variantId: variantId },
        () => {},
        () => this._render() // rollback on failure
      );
      tryAutoSubscribeBackInStock(this.dataset.productId, variantId, this.dataset.handle);
    }
  }
}

if (!window.customElements.get("wishlist-button")) {
  window.customElements.define("wishlist-button", WishlistButton);
}
